import { ReactNode, useEffect, useState, Suspense } from 'react';
import { Link, useLocation } from 'react-router';
import { useAuth } from '@/lib/auth';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { FileText, Inbox, Settings, LayoutDashboard, Boxes, List, Users, Layers, Shield, Menu, Key } from 'lucide-react';
import { TryggboIcon } from '@/components/TryggboIcon';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { NamespaceLoader } from '@/components/NamespaceLoader';
import { OrgSwitcher } from '@/components/OrgSwitcher';
import { ProfileDropdown } from '@/components/ProfileDropdown';
import { cn } from '@/lib/utils';
import { TooltipProvider } from '@/components/ui/tooltip';

function isNavActive(pathname: string, path: string): boolean {
  if (path === '/') return pathname === '/';
  if (path === '/admin') return pathname === '/admin';
  if (path === '/platform') return pathname.startsWith('/platform');
  return pathname === path || pathname.startsWith(path + '/');
}

const navLinkBase = "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors";
const navLinkActive = "bg-muted text-foreground";
const navLinkInactive = "text-muted-foreground hover:bg-muted hover:text-foreground";

// ──────────────────────────────────────────────
// Shared nav item definitions (single source of truth)
// ──────────────────────────────────────────────

interface NavItem {
  path: string;
  labelKey: string;
  icon: typeof FileText;
  badge?: number;
}

function useNavItems(pendingOffersCount: number) {
  const userItems: NavItem[] = [
    { path: '/', labelKey: 'listings', icon: FileText },
    { path: '/offers', labelKey: 'myOffers', icon: Inbox, badge: pendingOffersCount > 0 ? pendingOffersCount : undefined },
    { path: '/allocations', labelKey: 'myAllocations', icon: Key },
  ];

  const adminItems: NavItem[] = [
    { path: '/admin', labelKey: 'dashboard', icon: LayoutDashboard },
    { path: '/admin/resources', labelKey: 'resources', icon: Boxes },
    { path: '/admin/resource-types', labelKey: 'resourceTypes', icon: Layers },
    { path: '/admin/listings', labelKey: 'listings', icon: List },
    { path: '/admin/users', labelKey: 'users', icon: Users },
    { path: '/admin/settings', labelKey: 'settings', icon: Settings },
  ];

  return { userItems, adminItems };
}

/** Reusable nav skeleton shown while navigation namespace loads */
function NavSkeleton({ count = 3 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-24" />
        </div>
      ))}
    </>
  );
}

// ──────────────────────────────────────────────
// Shared nav renderer
// ──────────────────────────────────────────────

function NavItemLink({ item, t }: { item: NavItem; t: (key: string) => string }) {
  const location = useLocation();
  const Icon = item.icon;
  const active = isNavActive(location.pathname, item.path);
  return (
    <Link key={item.path} to={item.path} className={cn(navLinkBase, active ? navLinkActive : navLinkInactive)}>
      <Icon className="h-4 w-4" />
      {t(item.labelKey)}
      {item.badge && (
        <Badge variant="destructive" className="ml-auto h-5 w-5 flex items-center justify-center p-0 text-xs">
          {item.badge}
        </Badge>
      )}
    </Link>
  );
}

/** Desktop sidebar nav content – only rendered after namespace is ready */
function DesktopNavContent({ pendingOffersCount }: { pendingOffersCount: number }) {
  const { isAdmin, isSuperAdmin } = useAuth();
  const { t } = useTranslation(['navigation']);
  const { userItems, adminItems } = useNavItems(pendingOffersCount);

  return (
    <>
      {userItems.map(item => <NavItemLink key={item.path} item={item} t={t} />)}
      {isAdmin && (
        <>
          <div className="-mx-4 border-t border-border my-2" />
          <span className="text-xs font-medium text-muted-foreground px-3 py-1">{t('admin')}</span>
          {adminItems.map(item => <NavItemLink key={item.path} item={item} t={t} />)}
        </>
      )}
      {isSuperAdmin && (
        <>
          <div className="-mx-4 border-t border-border my-2" />
          <NavItemLink item={{ path: '/platform', labelKey: 'platform', icon: Shield }} t={t} />
        </>
      )}
    </>
  );
}

/** Mobile drawer nav content – only rendered after namespace is ready */
function MobileNavContent({ pendingOffersCount }: { pendingOffersCount: number }) {
  const { isAdmin, isSuperAdmin } = useAuth();
  const { t } = useTranslation(['navigation']);
  const location = useLocation();
  const { userItems, adminItems } = useNavItems(pendingOffersCount);

  const renderMobileItem = (item: NavItem) => {
    const Icon = item.icon;
    const active = isNavActive(location.pathname, item.path);
    return (
      <Link key={item.path} to={item.path}>
        <Button variant={active ? 'secondary' : 'ghost'} size="sm" className="w-full justify-start gap-2 relative">
          <Icon className="h-4 w-4" />
          {t(item.labelKey)}
          {item.badge && (
            <Badge variant="destructive" className="ml-auto h-5 w-5 flex items-center justify-center p-0 text-xs">
              {item.badge}
            </Badge>
          )}
        </Button>
      </Link>
    );
  };

  return (
    <>
      {userItems.map(renderMobileItem)}

      {isAdmin && (
        <>
          <div className="border-t border-border my-2" />
          <span className="text-xs font-medium text-muted-foreground px-3 py-1">{t('admin')}</span>
          {adminItems.map(renderMobileItem)}
        </>
      )}

      {isSuperAdmin && (
        <>
          <div className="border-t border-border my-2" />
          <Link to="/platform">
            <Button variant={isNavActive(location.pathname, '/platform') ? 'secondary' : 'ghost'} size="sm" className="w-full justify-start gap-2">
              <Shield className="h-4 w-4" />
              {t('platform')}
            </Button>
          </Link>
        </>
      )}
    </>
  );
}

interface AppShellProps {
  children: ReactNode;
}

/** Small component so t('appName') is only called inside NamespaceLoader */
function AppNameLabel() {
  const { t } = useTranslation(['navigation']);
  return <>{t('appName')}</>;
}

export function AppShell({ children }: AppShellProps) {
  const { user } = useAuth();
  const [pendingOffersCount, setPendingOffersCount] = useState(0);

  useEffect(() => {
    if (!user) {
      setPendingOffersCount(0);
      return;
    }

    const fetchPendingOffers = async () => {
      const { count } = await supabase
        .from('offers')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('status', 'pending');
      setPendingOffersCount(count || 0);
    };

    fetchPendingOffers();

    const channel = supabase
      .channel('offers-count')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'offers' }, () => {
        fetchPendingOffers();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const navSkeleton = <NavSkeleton count={3} />;

  return (
    <TooltipProvider>
    <div className="min-h-screen flex flex-col bg-background md:h-screen md:overflow-hidden">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-card">
        <div className="flex h-16 items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2">
            {/* Mobile hamburger menu */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden h-11 w-11 [&_svg]:size-6">
                  <Menu />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <TryggboIcon size={32} />
                    <NamespaceLoader namespaces={['navigation']} skeleton={<Skeleton className="h-5 w-20" />}>
                      <AppNameLabel />
                    </NamespaceLoader>
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-1 mt-6">
                  <NamespaceLoader namespaces={['navigation']} skeleton={navSkeleton}>
                    <MobileNavContent pendingOffersCount={pendingOffersCount} />
                  </NamespaceLoader>
                </nav>
              </SheetContent>
            </Sheet>

            <Link to="/" className="flex items-center gap-2 font-semibold text-foreground">
              <span className="shrink-0" style={{ width: 36, height: 36 }}>
                <TryggboIcon size={36} />
              </span>
              <span className="hidden sm:inline">
                <NamespaceLoader namespaces={['navigation']} skeleton={<Skeleton className="h-5 w-20" />}>
                  <AppNameLabel />
                </NamespaceLoader>
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-2">
            <OrgSwitcher />
            <ProfileDropdown />
          </div>
        </div>
      </header>

      {/* Content row: sidebar + main */}
      <div className="flex flex-1">
        {/* Desktop sidebar */}
        <aside className="hidden md:flex md:flex-col fixed left-0 top-16 z-40 w-64 h-[calc(100vh-4rem)] overflow-y-auto border-r border-border bg-card p-4">
          <nav className="flex flex-col gap-1">
            <NamespaceLoader namespaces={['navigation']} skeleton={navSkeleton}>
              <DesktopNavContent pendingOffersCount={pendingOffersCount} />
            </NamespaceLoader>
          </nav>
        </aside>

        <main className="flex-1 min-w-0 md:ml-64 md:h-[calc(100vh-4rem)] md:overflow-y-auto">
          <div className="container mx-auto px-4 py-8">
            <Suspense fallback={
              <div className="flex items-center justify-center py-16">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              </div>
            }>
              {children}
            </Suspense>
          </div>
        </main>
      </div>

    </div>
    </TooltipProvider>
  );
}
